import {
	Environment,
	type EnvironmentConfig,
	type LogFunction,
	type MutableRecordSource,
	Network,
	type OperationLoader,
	RelayFeatureFlags,
	type RelayFieldLogger,
	RecordSource as RelayRecordSource,
	type RenderPolicy,
	type FetchFunction,
} from 'relay-runtime';
import LiveResolverStore, {
	type ActorIdentifier,
	type GetDataID,
	// @ts-ignore
} from 'relay-runtime/lib/store/live-resolvers/LiveResolverStore';
import type { Scheduler } from 'relay-runtime/lib/store/RelayStoreTypes';

import {
	configureWebSocketClient,
	type WsClientOptions,
} from '@atlassian/graphql-subscription/utils/client';
import RelayDataID from '@atlassian/relay-data-id';
import type { ExternalDependencies } from '@atlassian/relay-fetch/multi-fetch/ExternalDependencies';
import { fetch as multiFetch } from '@atlassian/relay-fetch/multi-fetch/fetch';
import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import RelayScheduler from '@atlassian/relay-scheduler';
import { createSubscribeFn, type SubscribeOptions } from '@atlassian/relay-subscription';

import { createRelayHandlerProvider } from './handler-provider/relayHandlerProvider';
import { ufoDataFetchHoldMiddleware } from './middleware/ufoDataFetchHoldMiddleware';

// Enable Relay Resolvers globally for this module
RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;

type NetworkOptions = ExternalDependencies & SubscribeOptions;

function createNetwork(networkOptions: NetworkOptions, getEnableUfoHolds?: () => boolean) {
	const fetchOperation: FetchFunction = (request, variables, cacheConfig) =>
		multiFetch(request, variables, cacheConfig, networkOptions);

	const fetchOperationWithMiddleware = ufoDataFetchHoldMiddleware(fetchOperation, {
		getIsEnabled: getEnableUfoHolds,
	});

	return Network.create(
		fetchOperationWithMiddleware,
		networkOptions.isServer === false ? createSubscribeFn(networkOptions) : undefined,
	);
}

type Props = {
	log?: EnvironmentConfig['log'];
	requiredFieldLogger?: RelayFieldLogger;
	networkOptions?: NetworkOptions;
	/**
	 * @deprecated Do not override that option, it's here only for Townsquare backwards compatibility and will be removed soon.
	 */
	getDataID?: EnvironmentConfig['getDataID'];
	/**
	 * Returns the render policy Relay applies to a read that is missing data. Omit to keep Relay's
	 * own default of `'partial'`, which renders the incomplete result with absent fields resolved to
	 * `null`; `'full'` makes Relay suspend or fail the read instead.
	 *
	 * This is a function rather than a value because Relay's `UNSTABLE_defaultRenderPolicy` config
	 * field is captured once in the `Environment` constructor, and products need to vary the policy
	 * dynamically (e.g. for feature gating which can't be module-scoped).
	 */
	getDefaultRenderPolicy?: () => RenderPolicy;
	/**
	 * Set to true if you want to automatically track Relay network requests as UFO holds.
	 * This will soon become true by default.
	 */
	getEnableUfoHolds?: () => boolean;
	/** Where to report page-wide AGG socket lifecycle events. */
	wsClientOptions?: WsClientOptions;
};

export type AtlassianRelayEnvironment = Environment;

/**
 * Brand attached to every environment produced by {@link createRelayEnvironment}.
 *
 * We use `Symbol.for` (the global symbol registry) rather than a module-local `Symbol()` so the
 * brand stays stable even if this module ends up duplicated across bundles/realms — the guard in a
 * consumer will still recognise an environment created by another copy of this package.
 */
const ATLASSIAN_RELAY_ENVIRONMENT_BRAND: symbol = Symbol.for(
	'@atlassian/relay-environment.AtlassianRelayEnvironment',
);

function brandEnvironment(environment: AtlassianRelayEnvironment): AtlassianRelayEnvironment {
	Object.defineProperty(environment, ATLASSIAN_RELAY_ENVIRONMENT_BRAND, {
		value: true,
		enumerable: false,
		configurable: false,
		writable: false,
	});
	return environment;
}

/**
 * Type guard that returns `true` only for relay environments produced by
 * {@link createRelayEnvironment}.
 *
 * Use this to distinguish an Atlassian-configured relay environment (with the shared AGG network,
 * store, handlers, etc.) from an arbitrary `IEnvironment`/`Environment` that may be present in the
 * React tree.
 */
export function isAtlassianRelayEnvironment(
	environment: unknown,
): environment is AtlassianRelayEnvironment {
	return (
		environment != null &&
		(typeof environment === 'object' || typeof environment === 'function') &&
		(environment as Record<symbol, unknown>)[ATLASSIAN_RELAY_ENVIRONMENT_BRAND] === true
	);
}

/**
 * Relay calls `environment.UNSTABLE_getDefaultRenderPolicy()` on every `QueryResource.prepare`, so
 * this allows overriding it dynamically (and not during module scope evaluation).
 */
class DynamicRenderPolicyEnvironment extends Environment {
	private readonly getRenderPolicy: () => RenderPolicy;

	constructor(config: EnvironmentConfig, getRenderPolicy: () => RenderPolicy) {
		super(config);
		this.getRenderPolicy = getRenderPolicy;
	}

	/**
	 * Note: currently missing from @types/relay-runtime (version 20.1.1) but DOES exist at runtime.
	 */
	UNSTABLE_getDefaultRenderPolicy(): RenderPolicy {
		return this.getRenderPolicy();
	}
}

const createStore = (
	source: MutableRecordSource,
	options?: {
		actorIdentifier?: ActorIdentifier;
		gcReleaseBufferSize?: number;
		gcScheduler?: Scheduler;
		getDataID?: GetDataID;
		log?: LogFunction;
		operationLoader?: OperationLoader;
		queryCacheExpirationTime?: number;
		shouldProcessClientComponents?: boolean;
		treatMissingFieldsAsNull?: boolean;
	},
) => new LiveResolverStore(source, options);

export function createRelayEnvironment(environmentOptions: Props): AtlassianRelayEnvironment;

export function createRelayEnvironment(
	environmentOptions: Omit<Props, 'networkOptions'>,
): AtlassianRelayEnvironment;

export function createRelayEnvironment({
	log,
	requiredFieldLogger,
	networkOptions,
	getDataID,
	getDefaultRenderPolicy,
	getEnableUfoHolds,
	wsClientOptions,
}: Props): AtlassianRelayEnvironment {
	if (wsClientOptions) {
		configureWebSocketClient(wsClientOptions);
	}

	const source = new RelayRecordSource();
	const config: EnvironmentConfig = {
		configName: 'Atlassian Shared Graphql Gateway',
		network: createNetwork(networkOptions || { isServer: false }, getEnableUfoHolds),
		store: createStore(source, {
			log: log ?? undefined,
			getDataID: 'getDataID' in arguments[0] ? getDataID : RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: 'getDataID' in arguments[0] ? getDataID : RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
		handlerProvider: createRelayHandlerProvider(source),
	};

	const environment = getDefaultRenderPolicy
		? new DynamicRenderPolicyEnvironment(config, getDefaultRenderPolicy)
		: new Environment(config);

	return brandEnvironment(environment);
}
